import React, { useState, useMemo } from 'react';
import Table from '../../components/Table/Table';
import ClientForm from '../../components/Form/ClientForm';
import ConfirmDialog from '../../components/Modal/ConfirmDialog';
import TableSkeleton from '../../components/Loading/TableSkeleton';
import { PageHeader, FilterBar, ErrorState } from '../../components/Common';
import { TableColumn, Client } from '../../types';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import {
  useClients,
  useCreateClient,
  useUpdateClient,
  useDeleteClient,
} from '../../hooks/useClients';
import { useToast } from '../../contexts/ToastContext';

const Clients: React.FC = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | undefined>();
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<{ isOpen: boolean; client: Client | null }>({ isOpen: false, client: null });

  const toast = useToast();

  const queryOptions = useMemo(() => ({
    sort: [{ field: 'createdAt', order: 'desc' as const }],
    pagination: { pageSize: 100 },
  }), []);

  const { data: clients, isLoading, error, refetch } = useClients(queryOptions);

  const createMutation = useCreateClient(() => {
    setShowForm(false);
    setTimeout(() => refetch(), 100);
  });

  const updateMutation = useUpdateClient(() => {
    setShowForm(false);
    setEditingClient(undefined);
    setTimeout(() => refetch(), 100);
  });

  const deleteMutation = useDeleteClient(() => {
    setDeleteConfirm({ isOpen: false, client: null });
    setTimeout(() => refetch(), 100);
  });

  const filteredClients = useMemo(() => {
    if (!searchTerm) return clients;
    const term = searchTerm.toLowerCase();
    return clients.filter(c =>
      (c.name?.toLowerCase().includes(term)) ||
      (c.contactPerson?.toLowerCase().includes(term)) ||
      (c.email?.toLowerCase().includes(term)) ||
      (c.phone?.toLowerCase().includes(term)) ||
      (c.address?.toLowerCase().includes(term)) ||
      (c.contacts || []).some(contact =>
        (contact.contactPerson?.toLowerCase().includes(term)) ||
        (contact.email?.toLowerCase().includes(term)) ||
        (contact.phone?.toLowerCase().includes(term))
      )
    );
  }, [clients, searchTerm]);

  const renderContactList = (client: Client, field: 'contactPerson' | 'email' | 'phone') => {
    const values = (client.contacts && client.contacts.length > 0
      ? client.contacts.map(c => c[field])
      : [client[field]]
    ).filter(Boolean) as string[];
    if (values.length === 0) return '-';
    return (
      <div className="space-y-0.5">
        {values.map((value, i) => (
          <div key={i} className="text-sm">{value}</div>
        ))}
      </div>
    );
  };

  const columns: TableColumn[] = [
    { key: 'name', label: 'Company Name', sortable: true },
    { key: 'contactPerson', label: 'Contact Person', sortable: true, render: (_: any, row: Client) => renderContactList(row, 'contactPerson') },
    { key: 'email', label: 'Email', sortable: true, render: (_: any, row: Client) => renderContactList(row, 'email') },
    { key: 'phone', label: 'Phone', sortable: false, render: (_: any, row: Client) => renderContactList(row, 'phone') },
    { key: 'status', label: 'Status', sortable: true, render: (value: string) => (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${value === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
        {value}
      </span>
    )},
    { key: 'createdAt', label: 'Created', sortable: true, render: (value: string) => new Date(value).toLocaleDateString() },
  ];

  const handleEdit = (client: Client) => {
    setEditingClient(client);
    setShowForm(true);
  };

  const handleDelete = (client: Client) => {
    setDeleteConfirm({ isOpen: true, client });
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirm.client?.id) return;
    try {
      await deleteMutation.execute(deleteConfirm.client);
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const handleAdd = () => {
    setEditingClient(undefined);
    setShowForm(true);
  };

  const handleFormSubmit = async (data: Partial<Client>) => {
    try {
      if (editingClient?.id) {
        await updateMutation.execute({ id: editingClient.id, data });
      } else {
        await createMutation.execute({
          name: data.name,
          email: data.email,
          phone: data.phone,
          address: data.address,
          contactPerson: data.contactPerson,
          contacts: data.contacts || [],
          status: data.status || 'active',
        });
      }
    } catch (error) {
      console.error('Form submission failed:', error);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingClient(undefined);
  };

  const buildExportData = () =>
    filteredClients.map((client) => {
      const contacts = client.contacts && client.contacts.length > 0
        ? client.contacts
        : [{ contactPerson: client.contactPerson, email: client.email, phone: client.phone }];
      return {
        'Company Name': client.name,
        'Contact Person': contacts.map(c => c.contactPerson).filter(Boolean).join(' | '),
        Email: contacts.map(c => c.email).filter(Boolean).join(' | '),
        Phone: contacts.map(c => c.phone).filter(Boolean).join(' | '),
        Address: client.address,
        Status: client.status,
        Created: new Date(client.createdAt || '').toLocaleDateString(),
      };
    });

  const handleExport = () => {
    try {
      exportToExcel(buildExportData(), `clients_${new Date().toISOString().split('T')[0]}`);
      toast.success('Clients exported successfully');
    } catch (error) {
      toast.error('Failed to export clients');
    }
  };

  const handlePrint = () => {
    try {
      printAsExcel(buildExportData(), 'Clients');
    } catch (error) {
      toast.error('Failed to print clients');
    }
  };

  const handleClearFilters = () => setSearchTerm('');

  if (showForm) {
    return (
      <div className="space-y-6 fade-in">
        <ClientForm client={editingClient} onSubmit={handleFormSubmit} onCancel={handleFormCancel} />
      </div>
    );
  }

  if (isLoading) return <TableSkeleton rows={10} columns={6} />;
  if (error) return <ErrorState message={error.message} onRetry={refetch} entityName="clients" />;

  return (
    <div className="space-y-6 fade-in">
      <PageHeader
        title="Clients"
        description="Manage your client relationships and contact information"
      />
      <FilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder="Search clients..."
        filters={[]}
        onClearFilters={handleClearFilters}
        totalCount={clients.length}
        filteredCount={filteredClients.length}
        entityName="Clients"
        onAdd={handleAdd}
        addLabel="Add Client"
        onExport={handleExport}
        onPrint={handlePrint}
      />
      <Table
        columns={columns}
        data={filteredClients}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onAdd={handleAdd}
        title="Client Directory"
        searchPlaceholder=""
        searchable={false}
      />
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title="Delete Client"
        message={`Are you sure you want to delete "${deleteConfirm.client?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        type="danger"
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteConfirm({ isOpen: false, client: null })}
        isLoading={deleteMutation.isLoading}
      />
    </div>
  );
};

export default Clients;
